import { useState, useCallback } from 'react';
import LoqateApi from 'services/api/thirdparty/Loqate';
import ReactDOM from 'react-dom';
import LanguageLocale from 'utils/LanguageLocale';
import anaConsts from 'analytics/constants';
import analyticsUtils from 'analytics/utils';
import processEvent from 'analytics/processEvent';

const { getCurrentLanguage, LANGUAGES } = LanguageLocale;

const AVS_ELEMENTS_ON_VIEW = 6;

export function useLoqateAddresses({
    address,
    loqateWaitingForRef,
    addressPredictionRef,
    updateLoqateVerifiedAddressState,
    setFieldFocus,
    handleAddress1Blur,
    addressInputRef,
    section
}) {
    const [loqateAddresses, setLoqateAddresses] = useState([]);
    const [highlightedAddrIndex, setHighlightedAddrIndexState] = useState(-1);
    const [highlightTexts, setHighlightTextsState] = useState([]);

    const updateScrollAVS = useCallback(
        pos => {
            const avsContainer = ReactDOM.findDOMNode(addressPredictionRef.current);

            if (!avsContainer || !avsContainer.hasChildNodes()) {
                return;
            }

            if (pos !== undefined) {
                avsContainer.scrollTop = pos;
            } else {
                // Optional chaining guards against a race where highlightedAddrIndex outlives the
                // children array (e.g., results shrink before the scroll-into-view callback runs);
                // an undefined height propagates as NaN and all comparisons below short-circuit.
                const addressItemHeight = avsContainer.children[highlightedAddrIndex]?.offsetHeight;
                const padding = 8;
                const avsVisibleHeight = AVS_ELEMENTS_ON_VIEW * addressItemHeight;
                const currentAddressPos = highlightedAddrIndex * addressItemHeight + padding;
                const maxViewPos = avsContainer.scrollTop + avsVisibleHeight + padding;

                // is the selected address' lower point after the bottom visible area limit
                if (maxViewPos < currentAddressPos + addressItemHeight + padding) {
                    avsContainer.scrollTop = currentAddressPos + addressItemHeight - avsVisibleHeight;
                    // is the selected address' top point before the top visible area limit
                } else if (avsContainer.scrollTop + padding > currentAddressPos) {
                    avsContainer.scrollTop = currentAddressPos - padding;
                }
            }
        },
        [highlightedAddrIndex]
    );

    const setHighlightedAddrIndex = useCallback(
        (index, callUpdateAVS = false) => {
            setHighlightedAddrIndexState(index);

            if (callUpdateAVS) {
                updateScrollAVS();
            }
        },
        [setHighlightedAddrIndexState, updateScrollAVS]
    );

    const getLoqateAddresses = useCallback(
        (text, container) => {
            LoqateApi.findAddresses(text, address.country, container)
                .then(data => {
                    if (!container && text !== loqateWaitingForRef.current) {
                        return;
                    }

                    if (data.Items && data.Items.length === 1 && typeof data.Items[0].Error !== 'undefined') {
                        setLoqateAddresses([]);
                    } else {
                        if (data.Items && data.Items.length > 0) {
                            setLoqateAddresses(data.Items);
                            setHighlightedAddrIndex(0, () => updateScrollAVS(0));
                        } else {
                            setLoqateAddresses([]);
                        }
                    }
                })
                .catch(err => Sephora.logger.error('[useLoqateAddresses] findAddresses failed', err));
        },
        [address.country, loqateWaitingForRef, setHighlightedAddrIndex, updateScrollAVS]
    );

    const verifyLoqateAddress = useCallback(
        id => {
            LoqateApi.retrieveAddress(id)
                .then(data => {
                    if (data.Items && data.Items.length === 1 && typeof data.Items[0].Error !== 'undefined') {
                        // @TODO: handle error
                    } else {
                        // Check if there were any items found
                        if (data.Items && data.Items.length > 0) {
                            const currentLanguage = getCurrentLanguage().toUpperCase() === LANGUAGES.EN ? LANGUAGES.ENG : LANGUAGES.FRE;
                            // Update Fields with Loqate data
                            const addressObj = data.Items.filter(addressItem => addressItem.Language === currentLanguage)[0] || data.Items[0];
                            let addressLine1 = '';
                            const addressLine2 = addressObj.SubBuilding || '';
                            const city = addressObj.City || '';
                            const postalCode = addressObj.PostalCode || '';
                            const stateLocal = addressObj.Province || '';
                            const isPOBoxAddress = !!addressObj.POBoxNumber;
                            const addressType = addressObj.Type;

                            if (addressObj.BuildingNumber && addressObj.Street) {
                                addressLine1 = `${addressObj.BuildingNumber} ${addressObj.Street}`;
                            } else if (addressObj.Line1) {
                                addressLine1 = addressObj.Line1;
                            }

                            updateLoqateVerifiedAddressState({
                                addressLine1,
                                addressLine2,
                                city,
                                postalCode,
                                stateLocal,
                                isPOBoxAddress,
                                addressType
                            });
                        }
                    }
                })
                .catch(err => Sephora.logger.error('[useLoqateAddresses] retrieveAddress failed', err));
        },
        [updateLoqateVerifiedAddressState]
    );

    const handleLoqateAddressClick = useCallback(
        loqateAddress => {
            /* If the Type is not 'Address' it means when can call the Loqate service again because
               there are more addresses under this address (multiple units) */
            if (loqateAddress.Type !== 'Address') {
                setHighlightTexts(loqateAddress);

                getLoqateAddresses(loqateAddress.Text, loqateAddress.Id);
                // return the focus back to the address1 field because we need to show more addresses
                setFieldFocus(addressInputRef.current);
            } else {
                verifyLoqateAddress(loqateAddress.Id);
                // Hide AVS to show autocomplete fields
                handleAddress1Blur();
            }

            fireSelectLoqateAddressAnalytics();
        },
        [setFieldFocus, handleAddress1Blur, verifyLoqateAddress, getLoqateAddresses]
    );

    const fireSelectLoqateAddressAnalytics = () => {
        const actionString = `${section}:${anaConsts.ACTION_INFO.SELECT_LOQATE_ADDRESS}`;
        const mostRecentAsyncLoadEvent = analyticsUtils.getMostRecentEvent('asyncPageLoad');

        const eventData = {
            data: {
                actionInfo: actionString,
                previousPage: mostRecentAsyncLoadEvent?.eventInfo?.attributes?.previousPageName
            }
        };

        processEvent.process(anaConsts.LINK_TRACKING_EVENT, eventData);
    };

    const setHighlightTexts = useCallback(loqateAddress => {
        const highlights = loqateAddress.Highlight.split(';').shift();
        const highlightTextsData = [];

        if (highlights) {
            highlights.split(',').forEach(highlight => {
                const limits = highlight.split('-');
                highlightTextsData.push(loqateAddress.Text.substring(limits[0], limits[1]));
            });

            setHighlightTextsState(highlightTextsData);
        }
    }, []);

    const getHighlight = useCallback(
        loqateAddress => {
            const highlightIndex = [];

            if (loqateAddress.Highlight.trim()) {
                return loqateAddress.Highlight;
            }

            highlightTexts.forEach(highlightText => {
                const regex = new RegExp(highlightText, 'g');
                let match;

                while ((match = regex.exec(loqateAddress.Text))) {
                    highlightIndex.push(`${regex.lastIndex - match[0].length}-${regex.lastIndex}`);
                }
            });

            return highlightIndex.join(',');
        },
        [highlightTexts]
    );

    return {
        loqateAddresses,
        highlightedAddrIndex,
        highlightTexts,
        getLoqateAddresses,
        setLoqateAddresses,
        setHighlightedAddrIndexState,
        handleLoqateAddressClick,
        getHighlight,
        setHighlightedAddrIndex
    };
}
